/**
 * STRUCTURE ops: the part of a template patch that changes what a game is
 * made of, not just its numbers and words — the "adapt" and "extend" levels
 * of the one Create route. A patch may add or remove an archetype, attach,
 * detach or reconfigure a module on an archetype, add, remove or reconfigure
 * a global (the objective, the layout, the director's phases), and set an
 * archetype's tags, size or render. Every module named must be in the game's
 * library; the result must validate and prove like any other spec.
 *
 * Ops name the TEMPLATE's kinds (renames apply after every op, as for the
 * rest of a patch). The patch also names its new SIGNATURE modules: the ones
 * the adaptation's game depends on, each stub-checked by the proof; and the
 * EVENTS its game must show on every proof seed — what a rule it adds must
 * do in play when the objective does not depend on it.
 */
import type { JsonObject } from '../../json.js';
import type { AnyComposeModule } from '../module.js';
import type { ArchetypeSpecV1, GameSpecV1, HudRefV1, ModuleUseV1 } from '../spec.js';
import { VERB_PROOF_MS, verbLeadProblem, type VerbProofV1 } from './template.js';

export type StructureOpV1 =
  | { op: 'addArchetype'; kind: string; archetype: ArchetypeSpecV1 }
  | { op: 'removeArchetype'; kind: string }
  | { op: 'attach'; kind: string; module: ModuleUseV1 }
  | { op: 'detach'; kind: string; module: string }
  | { op: 'configure'; kind: string; module: string; config: JsonObject }
  | { op: 'addGlobal'; global: ModuleUseV1 }
  | { op: 'removeGlobal'; key: string }
  | { op: 'configureGlobal'; key: string; config: JsonObject }
  | { op: 'setArchetype'; kind: string; fields: Partial<Pick<ArchetypeSpecV1, 'tags' | 'size' | 'render' | 'role' | 'elevation'>> }
  /** The whole HUD, restated (an adaptation that removes or adds a module
   * says which fields the player sees). */
  | { op: 'setHud'; hud: HudRefV1[] };

export interface TemplateStructureV1 {
  ops: StructureOpV1[];
  /** Modules (attached or added by `ops`) the adapted game depends on: each
   * must make the proof fail when stubbed. */
  signature: string[];
  /** Events (each emitted by a module of the adapted game) every proof seed
   * must observe; absent when none. */
  events?: string[];
  /** The adapted game's native verb proofs, restated in full (one per
   * disclosed core verb, in order) — only when a change makes one of the
   * template's no longer hold; absent: the template's. */
  verbProofs?: VerbProofV1[];
}

export const STRUCTURE_LIMITS = Object.freeze({ ops: 48, signature: 8, events: 16 });
const EVENT = /^[a-z][a-z0-9-]{0,39}$/;

const KIND = /^[a-z][a-z0-9-]{0,39}$/;
const OPS = ['addArchetype', 'removeArchetype', 'attach', 'detach', 'configure', 'addGlobal', 'removeGlobal', 'configureGlobal', 'setArchetype',
  'setHud'];
const ARCHETYPE_FIELDS = ['tags', 'size', 'render', 'role', 'elevation'];

function plain(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value) && [Object.prototype, null].includes(Object.getPrototypeOf(value));
}

const globalKey = (g: ModuleUseV1) => g.key ?? g.use;

/** Validate an untrusted structure value (every error collected). Shapes
 * only: what the ops produce is checked by applying them and validating the
 * resulting spec. */
export function validateStructure(input: unknown, registry: Pick<ReadonlyMap<string, AnyComposeModule>, 'has'>):
  { ok: true; structure: TemplateStructureV1 } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  if (!plain(input)) return { ok: false, errors: ['structure: expected { ops, signature, events?, verbProofs? }'] };
  for (const key of Object.keys(input)) if (!['ops', 'signature', 'events', 'verbProofs'].includes(key)) errors.push(`structure.${key}: unsupported field`);
  const ops = input.ops, signature = input.signature, events = input.events ?? [];
  if (!Array.isArray(ops) || ops.length > STRUCTURE_LIMITS.ops) errors.push(`structure.ops: expected at most ${STRUCTURE_LIMITS.ops} ops`);
  if (!Array.isArray(signature) || signature.length > STRUCTURE_LIMITS.signature || signature.some(s => typeof s !== 'string' || !registry.has(s))) {
    errors.push(`structure.signature: expected at most ${STRUCTURE_LIMITS.signature} module ids from the library`);
  }
  if (!Array.isArray(events) || events.length > STRUCTURE_LIMITS.events || events.some(e => typeof e !== 'string' || !EVENT.test(e))
    || new Set(events).size !== events.length) {
    errors.push(`structure.events: expected at most ${STRUCTURE_LIMITS.events} distinct event names`);
  }
  const use = (value: unknown, at: string): void => {
    if (!plain(value) || typeof value.use !== 'string' || !plain(value.config)) { errors.push(`${at}: expected { use, config }`); return; }
    if (!registry.has(value.use)) errors.push(`${at}.use: ${value.use} is not a module in this game's library`);
  };
  (Array.isArray(ops) ? ops : []).forEach((raw, i) => {
    const at = `structure.ops[${i}]`;
    if (!plain(raw) || typeof raw.op !== 'string' || !OPS.includes(raw.op)) { errors.push(`${at}: expected an op (${OPS.join(', ')})`); return; }
    const kindOk = (k: unknown) => typeof k === 'string' && KIND.test(k) && k !== 'background';
    switch (raw.op) {
      case 'addArchetype':
        if (!kindOk(raw.kind)) errors.push(`${at}.kind: expected a kebab-case kind (never "background")`);
        if (!plain(raw.archetype) || !Array.isArray(raw.archetype.modules)) errors.push(`${at}.archetype: expected an archetype`);
        else (raw.archetype.modules as unknown[]).forEach((m, j) => use(m, `${at}.archetype.modules[${j}]`));
        break;
      case 'removeArchetype': if (!kindOk(raw.kind)) errors.push(`${at}.kind: expected a kind`); break;
      case 'attach': if (!kindOk(raw.kind)) errors.push(`${at}.kind: expected a kind`); use(raw.module, `${at}.module`); break;
      case 'detach': case 'configure':
        if (!kindOk(raw.kind) || typeof raw.module !== 'string') errors.push(`${at}: expected kind and module id`);
        if (raw.op === 'configure' && !plain(raw.config)) errors.push(`${at}.config: expected an object`);
        break;
      case 'addGlobal': use(raw.global, `${at}.global`); break;
      case 'removeGlobal': case 'configureGlobal':
        if (typeof raw.key !== 'string') errors.push(`${at}.key: expected a global key`);
        if (raw.op === 'configureGlobal' && !plain(raw.config)) errors.push(`${at}.config: expected an object`);
        break;
      case 'setArchetype':
        if (!kindOk(raw.kind) || !plain(raw.fields) || !Object.keys(raw.fields).length
          || Object.keys(raw.fields).some(k => !ARCHETYPE_FIELDS.includes(k))) errors.push(`${at}: expected kind and fields (${ARCHETYPE_FIELDS.join(', ')})`);
        break;
      case 'setHud':
        if (!Array.isArray(raw.hud) || raw.hud.some(h => !plain(h) || typeof h.from !== 'string' || typeof h.field !== 'string')) {
          errors.push(`${at}.hud: expected [{ from, field, label? }]`);
        }
        break;
    }
  });
  const verbProofs = input.verbProofs;
  if (verbProofs !== undefined) {
    if (!Array.isArray(verbProofs) || verbProofs.length < 1 || verbProofs.length > 12) errors.push('structure.verbProofs: expected 1..12 verb proofs');
    else verbProofs.forEach((p, i) => { const e = verbProofProblem(p); if (e) errors.push(`structure.verbProofs[${i}]: ${e}`); });
  }
  if (errors.length) return { ok: false, errors };
  return { ok: true, structure: { ops: structuredClone(ops) as StructureOpV1[], signature: [...signature as string[]],
    ...((events as string[]).length ? { events: [...events as string[]] } : {}),
    ...(verbProofs !== undefined ? { verbProofs: structuredClone(verbProofs) as VerbProofV1[] } : {}) } };
}

const VERB_INPUTS = ['left', 'right', 'up', 'down', 'a', 'b', 'tap'];

/** What is wrong with one verb proof's shape, or null. */
function verbProofProblem(p: unknown): string | null {
  if (!plain(p) || typeof p.verb !== 'string' || !p.verb.trim() || p.verb.length > 60) return 'expected { verb, input, ms, expect }';
  if (typeof p.input !== 'string' || !VERB_INPUTS.includes(p.input)) return `input must be one of ${VERB_INPUTS.join(', ')}`;
  if (p.input === 'tap' ? typeof p.target !== 'string' || !KIND.test(p.target) : p.target !== undefined) return 'a tap names its target kind (and only a tap does)';
  if (!Number.isInteger(p.ms) || (p.ms as number) < VERB_PROOF_MS.min || (p.ms as number) > VERB_PROOF_MS.max) return `ms must be a whole number ${VERB_PROOF_MS.min}..${VERB_PROOF_MS.max}`;
  const e = p.expect;
  if (!plain(e) || !((e.kind === 'event' && typeof e.event === 'string' && /^[a-z][a-z0-9-]{0,39}$/.test(e.event) && Object.keys(e).length === 2)
    || ((e.kind === 'state-change' || e.kind === 'state-diverges') && typeof e.path === 'string' && /^[a-zA-Z0-9_.]{1,80}$/.test(e.path) && Object.keys(e).length === 2))) {
    return 'expect must be { kind: "event", event }, { kind: "state-change", path } or { kind: "state-diverges", path }';
  }
  const lead = verbLeadProblem(p.lead, p.input === 'tap');
  if (lead) return lead;
  const keys = Object.keys(p).filter(k => k !== 'lead').sort().join(',');
  return keys === (p.input === 'tap' ? 'expect,input,ms,target,verb' : 'expect,input,ms,verb') ? null : 'unsupported fields';
}

/** Apply validated ops in order to a copy of `spec`; throws on an op whose
 * target is missing or already present (the caller reports it). */
export function applyStructure(input: GameSpecV1, structure: TemplateStructureV1): GameSpecV1 {
  const spec = structuredClone(input);
  const archetype = (kind: string): ArchetypeSpecV1 => {
    const a = spec.archetypes[kind];
    if (!a) throw new RangeError(`structure: no archetype ${kind}`);
    return a;
  };
  structure.ops.forEach((op, i) => {
    const at = `structure.ops[${i}]`;
    switch (op.op) {
      case 'addArchetype':
        if (spec.archetypes[op.kind]) throw new RangeError(`${at}: archetype ${op.kind} already exists`);
        spec.archetypes[op.kind] = structuredClone(op.archetype);
        break;
      case 'removeArchetype':
        archetype(op.kind);
        if (spec.players.archetypes.includes(op.kind)) throw new RangeError(`${at}: ${op.kind} is a player archetype`);
        delete spec.archetypes[op.kind];
        break;
      case 'attach': {
        const a = archetype(op.kind);
        if (a.modules.some(m => m.use === op.module.use)) throw new RangeError(`${at}: ${op.kind} already carries ${op.module.use}`);
        a.modules.push(structuredClone(op.module));
        break;
      }
      case 'detach': {
        const a = archetype(op.kind);
        if (!a.modules.some(m => m.use === op.module)) throw new RangeError(`${at}: ${op.kind} does not carry ${op.module}`);
        a.modules = a.modules.filter(m => m.use !== op.module);
        break;
      }
      case 'configure': {
        const m = archetype(op.kind).modules.find(u => u.use === op.module);
        if (!m) throw new RangeError(`${at}: ${op.kind} does not carry ${op.module}`);
        m.config = structuredClone(op.config);
        break;
      }
      case 'addGlobal':
        if (spec.globals.some(g => globalKey(g) === globalKey(op.global))) throw new RangeError(`${at}: global ${globalKey(op.global)} already exists`);
        spec.globals.push(structuredClone(op.global));
        break;
      case 'removeGlobal':
        if (!spec.globals.some(g => globalKey(g) === op.key)) throw new RangeError(`${at}: no global ${op.key}`);
        spec.globals = spec.globals.filter(g => globalKey(g) !== op.key);
        break;
      case 'configureGlobal': {
        const g = spec.globals.find(u => globalKey(u) === op.key);
        if (!g) throw new RangeError(`${at}: no global ${op.key}`);
        g.config = structuredClone(op.config);
        break;
      }
      case 'setArchetype':
        Object.assign(archetype(op.kind), structuredClone(op.fields));
        break;
      case 'setHud':
        spec.hud = structuredClone(op.hud);
        break;
    }
  });
  return spec;
}

/** Module ids a spec uses. */
export function specModuleIds(spec: GameSpecV1): Set<string> {
  return new Set([...spec.globals.map(g => g.use), ...Object.values(spec.archetypes).flatMap(a => a.modules.map(m => m.use))]);
}
